import { useNavigate } from 'react-router-dom';
import { formatEncounterDate } from '../utils/historyEvidence';
// Excel style wide multi-column table editor for complex objects, with option for a simple two column data editor
import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown, CircleCheck, CirclePlus, CircleX, Copy, EllipsisVertical, FilePlus2, History, MessageSquarePlus, Undo2, RefreshCw, X } from 'lucide-react';
import Tooltip from "@mui/material/Tooltip";
import { Menu, MenuItem, IconButton, ListItemIcon, ListItemText, Popover } from '@mui/material';
import { DataGrid, GridCellModes, useGridApiRef } from "@mui/x-data-grid";
import { updateData } from "../utils/firestoreService";
import { handleCopyToClipboard, saveTableData, toggleRowDelete, handleAddRow, handleTabNavigation } from "../utils/editableTableUtils";
import { generateSectionExport } from "../utils/exportUtils";
import { processHistoryData, processHistorySection, collectAllHistoryForRows } from "../utils/historyProcessing";
import { SECTION_FIELD_MAPPING, shouldFillRow, createFilledRow, getFieldData } from "../utils/fieldUtils";
import { v4 as uuidv4 } from "uuid";
import { useSidebar } from "../contexts/SidebarContext";
import SectionCommentButton from "../components/document/SectionCommentButton";

const EditableTableSection = ({
  section,
  sectionKey,
  data,
  dataHistory,
  patientId,
  docId,
  copyToClipboard,
  ClipboardCopy,
  userSettings,
  isEditable = true,
  isProcessing = false,
  sectionStatus = "pending", // 'pending', 'processing', 'completed'
  defaultOpen = true,
  onSectionView,             // Callback when section is opened (for approval review tracking)
  requiresReview = false,    // Whether this section requires review for approval
  isReviewed = false,        // Whether this section has been reviewed
  onReprocessSection,        // Callback to reprocess this section (AppShell v3 only)
  onAddSectionComment,       // Callback to add a section-level comment (Phase 4)
  onAddRowComment,           // Callback to add a row-level comment (Phase 4)
}) => {
  const navigate = useNavigate();
  const [combinedData, setCombinedData] = useState([]);
  const [copiedIcon, setCopiedIcon] = useState(false);
  const [defaultFillIcon, setDefaultFillIcon] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [cellModesModel, setCellModesModel] = useState({});
  const [touchedCells, setTouchedCells] = useState(() => new Set()); // Performance: Set for O(1) lookups
  const [pendingDeleteId, setPendingDeleteId] = useState(null); // For inline delete confirmation
  const pendingDeleteTimeout = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [rowHistoryMap, setRowHistoryMap] = useState(new Map());
  const [historyAnchorEl, setHistoryAnchorEl] = useState(null);
  const [historyPopoverKey, setHistoryPopoverKey] = useState(null);
  const apiRef = useGridApiRef();

  // Get the global setIsActivelyEditing function from context
  const { setIsActivelyEditing } = useSidebar();

  // Performant cell touch tracking
  const markCellTouched = useCallback((rowId, field) => {
    setTouchedCells((prev) => new Set(prev).add(`${rowId}:${field}`));
  }, []);

  // Track if any cell is in edit mode - only when editable
  useEffect(() => {
    if (!isEditable) {
      setIsActivelyEditing(`table-${section.dataField || section.title}`, false);
      return;
    }

    // Check if any cell is in edit mode
    const isAnyEditMode = Object.values(cellModesModel).some((row) => Object.values(row).some((cell) => cell.mode === GridCellModes.Edit));

    const editorId = `table-${section.dataField || section.title}`;

    setIsActivelyEditing(editorId, isAnyEditMode);
  }, [cellModesModel, setIsActivelyEditing, section.dataField, section.title, isEditable]);

  // Determine initial accordion state based on user preference and processing status
  const savedAccordionState = data?.settings?.[section.dataField]?.accordionOpen;
  const shouldRespectSavedState = userSettings?.collapsedSections !== false;

  let initialOpen;
  if (shouldRespectSavedState && savedAccordionState !== undefined) {
    initialOpen = savedAccordionState;
  } else if (shouldRespectSavedState) {
    // For processing documents, keep unprocessed sections closed
    initialOpen = sectionStatus === "completed" ? true : section.defaultOpen ?? defaultOpen;
  } else {
    // If collapsedSections is false, always open unless processing and not complete
    initialOpen = sectionStatus === "pending" ? false : true;
  }

  const [isOpen, setIsOpen] = useState(initialOpen);

  // Auto-open section when it completes during processing
  // Note: Only trigger on sectionStatus change, not isOpen change, to avoid re-opening after user closes
  useEffect(() => {
    if (sectionStatus === "completed") {
      const savedState = data?.settings?.[section.dataField]?.accordionOpen;
      // Only auto-open if user hasn't explicitly set a preference
      if (savedState === undefined) {
        setIsOpen(true);
      }
    }
  }, [sectionStatus, data?.settings, section.dataField]);

  // Process and load all data
  useEffect(() => {
    const processAllData = async () => {
      if (dataHistory === undefined || !data) return;

      try {
        let historyData = [];
        if (section.displayHistory && dataHistory) {
          const filteredHistory = dataHistory.map((record) => ({
            ...record[section.dataField],
            documentDate: record.documentDate,
            sourceDocumentId: record.id,
            reviewState: record.historyEvidence?.reviewState || "unreviewed",
          }));
          const getKey = (item) => (item ? item.id || item[section.primaryField] : null);

          // returns history data from previous records for this section
          historyData = await processHistoryData(filteredHistory, getKey);

          // Collect all historical summaries per row for the History Popup
          // Key by primaryField (not id) so renderCell can look up by row name
          const getKeyByName = (item) => (item ? item[section.primaryField] : null);
          const allHistory = collectAllHistoryForRows(filteredHistory, getKeyByName);
          setRowHistoryMap(allHistory);
        } else {
          setRowHistoryMap(new Map());
        }

        // Process and sort data using historyProcessing to select the best data to display from both the current and historical data
        const processedData = processHistorySection(section, data[section.dataField], historyData, true);

        // Filter empty rows on mount - hide rows without content in primary field or summary
        const filteredData = (() => {
          if (!Array.isArray(processedData)) return processedData;

          const primaryField = section.primaryField || "name";

          // Helper to check if a field has meaningful content
          const hasContent = (value) => {
            if (!value) return false;
            if (typeof value === "string") return value.trim() !== "";
            return true;
          };

          // Keep rows that have content in primary field OR summary field
          const entriesWithData = processedData.filter((item) => {
            return hasContent(item[primaryField]) || hasContent(item.summary);
          });

          // If no entries have data, keep one empty row for user to fill
          if (entriesWithData.length === 0 && processedData.length > 0) {
            return [processedData[0]];
          }

          return entriesWithData;
        })();

        // Ensure every row has a consistent ID
        const dataWithIds = filteredData.map((item, index) => ({
          ...item,
          id: item.id || uuidv4(),
        }));

        setCombinedData(dataWithIds);
        setIsInitialLoadComplete(true);
      } catch (error) {
        console.error(`Error processing data for section ${section.title}:`, error);
        setCombinedData([]);
        setIsInitialLoadComplete(true);
      }
    };

    processAllData();
  }, [dataHistory, section]);

  // Handles accordion open/close and persists state.
  const toggleAccordion = () => {
    const newOpen = !isOpen;
    setIsOpen(newOpen);
    if (isEditable) {
      updateData(`patients/${patientId}/documents`, docId, {
        [`settings.${section.dataField}.accordionOpen`]: newOpen,
      }).catch(error => console.warn('Could not save section display preference:', error.message));
    }
  };

  // Adds a new row to the top of the combined data
  const handleAddRowLocal = async () => {
    if (!isEditable) return; // Prevent adding rows if not editable
    const updatedData = await handleAddRow(section, combinedData, setCombinedData, patientId, docId);

    // Auto-focus primary field on new row (if exists)
    if (updatedData?.[0]?.id && section.primaryField) {
      const newRowId = updatedData[0].id;
      // Use requestAnimationFrame for better timing with React updates
      requestAnimationFrame(() => {
        // Focus and enter edit mode on the primary field
        apiRef.current?.setCellFocus(newRowId, section.primaryField);
        setCellModesModel({
          [newRowId]: { [section.primaryField]: { mode: GridCellModes.Edit } },
        });
      });
    }
  };

  // Optimized default fill function
  const defaultFill = useCallback(async () => {
    if (section.editorStyle !== "data") return;

    setDefaultFillIcon(true);
    setTimeout(() => setDefaultFillIcon(false), 1000);

    try {
      const fieldData = await getFieldData();
      const fieldKey = SECTION_FIELD_MAPPING[section.dataField];
      const sectionFields = fieldData[fieldKey];

      if (!sectionFields) {
        console.warn("No section fields found for:", section.dataField);
        return;
      }

      // Process only rows that need filling - more efficient
      const updatedData = combinedData.map((row) => {
        // Convert name to snake_case to match field keys
        const fieldKey = row.name ? row.name.toLowerCase().replace(/\s+/g, "_") : "";
        const shouldFill = shouldFillRow(row);
        const hasDefaultValue = fieldKey && sectionFields[fieldKey]?.defaultValue;

        if (!shouldFill || !hasDefaultValue) {
          return row;
        }
        return createFilledRow(row, sectionFields[fieldKey].defaultValue);
      });

      // Only update if changes were made
      const hasChanges = updatedData.some((row, index) => row.summary !== combinedData[index].summary);

      if (hasChanges) {
        setCombinedData(updatedData);
        await saveTableData(updatedData, section, patientId, docId);
      }
    } catch (error) {
      console.error("Error in default fill:", error);
    }
  }, [section, combinedData, patientId, docId]);

  const handleDeleteRow = async (rowId) => {
    // Two-click confirmation pattern
    if (pendingDeleteId === rowId) {
      // Second click - confirm delete
      clearTimeout(pendingDeleteTimeout.current);
      setPendingDeleteId(null);
      const rowIndex = combinedData.findIndex((row) => row.id === rowId);
      if (rowIndex !== -1) {
        const updatedCombinedData = toggleRowDelete(combinedData, rowIndex);
        setCombinedData(updatedCombinedData);
        await saveTableData(updatedCombinedData, section, patientId, docId);
      }
    } else {
      // First click - set pending and start timeout
      clearTimeout(pendingDeleteTimeout.current);
      setPendingDeleteId(rowId);
      pendingDeleteTimeout.current = setTimeout(() => {
        setPendingDeleteId(null);
      }, 3000); // Reset after 3 seconds
    }
  };

  const handleRestoreRow = async (rowId) => {
    const rowIndex = combinedData.findIndex((row) => row.id === rowId);
    if (rowIndex !== -1) {
      const updatedCombinedData = toggleRowDelete(combinedData, rowIndex); // toggleRowDelete flips isDeleted
      setCombinedData(updatedCombinedData);
      await saveTableData(updatedCombinedData, section, patientId, docId);
    }
  };

  const handleMenuOpen = (event, rowId) => {
    event.stopPropagation();
    setMenuPosition({ top: event.clientY, left: event.clientX });
    setMenuRowId(rowId);
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
    setMenuRowId(null);
  };

  const handleCopyRow = async (rowId) => {
    const rowIndex = combinedData.findIndex(r => r.id === rowId);
    if (rowIndex === -1) return;
    const row = combinedData[rowIndex];
    const duplicate = {
      ...row,
      id: uuidv4(),
      isHistory: false,
      humanReview: true,
      humanAdded: true,
    };
    delete duplicate.isNew;
    const updatedData = [
      ...combinedData.slice(0, rowIndex + 1),
      duplicate,
      ...combinedData.slice(rowIndex + 1),
    ];
    setCombinedData(updatedData);
    await saveTableData(updatedData, section, patientId, docId);
  };

  // Create columns configuration for DataGrid
  const createColumns = () => {
    const columns = Object.keys(section.fields).map((key, index) => {
      const field = section.fields[key];
      const isPrimaryField = key === section.primaryField;
      const isDataStyle = section.editorStyle === "data";

      return {
        field: key,
        headerName: field.name,
        flex: field.width / 100,
        minWidth: isPrimaryField ? 80 : 120, // Minimum to prevent word cutting while allowing responsive shrinking
        editable: field.type !== "check" && !(isPrimaryField && isDataStyle), // Disable editing for checkboxes and primary field in data style
        type: field.type === "check" ? "boolean" : "string",
        sortable: section.editorStyle !== "data", // Disable sorting for data style
        cellClassName: (params) => {
          if (index === 1 && params.row.humanReview === false) {
            return "column-not-reviewed";
          }
          return "";
        },

        renderEditCell: (params) => {
          // Custom edit cell for multiline support
          if (field.type !== "check") {
            const isHistory = params.row.isHistory;
            const isPrimaryField = key === section.primaryField;
            const isDataStyle = section.editorStyle === "data";

            // Build className based on conditions to match renderCell
            let className = "table-cell-view";
            if (isPrimaryField && isDataStyle) {
              className += " column-gray";
            }

            return (
              <textarea
                value={params.value || ""}
                onChange={(e) => {
                  params.api.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: e.target.value,
                  });
                }}
                onBlur={() => {
                  markCellTouched(params.id, params.field);
                  params.api.stopCellEditMode({ id: params.id, field: params.field });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    markCellTouched(params.id, params.field);
                    params.api.stopCellEditMode({ id: params.id, field: params.field });
                  }
                  if (e.key === "Tab") {
                    e.preventDefault();
                    markCellTouched(params.id, params.field);
                    params.api.stopCellEditMode({ id: params.id, field: params.field });
                    // Let the parent handle tab navigation
                    handleCellKeyDown(params, e);
                  }
                }}
                className={`table-edit-textarea ${className}${isHistory && !isPrimaryField ? ' table-cell-history' : ''}`}
                autoFocus
                onFocus={(e) => {
                  // Position cursor at the end
                  e.target.setSelectionRange(e.target.value.length, e.target.value.length);
                }}
                onInput={(e) => {
                  // Auto-resize height to fit content, with scrolling for very long content
                  e.target.style.height = "auto";
                  const newHeight = Math.min(e.target.scrollHeight, 300);
                  e.target.style.height = newHeight + "px";
                  // Enable scrolling if content exceeds max height
                  e.target.style.overflow = e.target.scrollHeight > 300 ? "auto" : "hidden";
                }}
              />
            );
          }
          return null;
        },

        renderCell: (params) => {
          const isHistory = params.row.isHistory;
          const isPrimaryField = key === section.primaryField;
          const isDataStyle = section.editorStyle === "data";
          const isTableStyle = section.editorStyle === "table";
          const isEmpty = !params.value || params.value === "";
          const isUserCreated = params.row.humanAdded === true;

          // Build className based on conditions
          let className = "table-cell-view";
          if (isPrimaryField && isDataStyle) {
            className += " column-gray";
          }
          // Add red outline only if cell was touched and is still empty
          const wasTouched = touchedCells.has(`${params.id}:${key}`);
          if (isPrimaryField && isTableStyle && isEmpty && isUserCreated && wasTouched) {
            className += " column-empty-primary";
          }

          // Handle boolean/checkbox fields
          if (field.type === "check") {
            return (
              <div
                className={`${className} table-cell-checkbox`}>
                <input
                  type="checkbox"
                  disabled={!isEditable}
                  className={`table-checkbox${isHistory ? ' table-cell-history' : ''}`}
                  checked={!!params.value}
                  onChange={(e) => {
                    handleCellEdit({ id: params.id, field: key, value: e.target.checked });
                  }}
                />
              </div>
            );
          }

          // Repeated values are still dated evidence and must remain accessible.
          const isSummaryField = key === "summary";
          const rowPrimaryValue = params.row[section.primaryField];
          const rowHistoryEntries = isSummaryField && section.displayHistory && rowPrimaryValue
            ? (rowHistoryMap.get(rowPrimaryValue) || [])
            : [];
          const hasHistoryDiff = rowHistoryEntries.length > 0;

          return (
            <div
              className={`${className}${isHistory && !isPrimaryField ? ' table-cell-history' : ''}${isSummaryField ? ' table-cell-summary-wrap' : ''}`}>
              {params.value ?? ""}
              {hasHistoryDiff && (
                <span
                  className="history-popup-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setHistoryAnchorEl(e.currentTarget);
                    setHistoryPopoverKey(rowPrimaryValue);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="View historical values"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      setHistoryAnchorEl(e.currentTarget);
                      setHistoryPopoverKey(rowPrimaryValue);
                    }
                  }}
                >
                  <History size={12} />
                </span>
              )}
            </div>
          );
        },
      };
    });

    // Add three-dot menu column for table-style sections
    if (isEditable && section.editorStyle === "table") {
      columns.push({
        field: "actions",
        headerName: "",
        width: 18,
        flex: 0,
        minWidth: 18,
        maxWidth: 18,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <IconButton
            className="row-actions-trigger"
            size="small"
            onClick={(e) => handleMenuOpen(e, params.row.id)}
            aria-label="Row actions"
            tabIndex={0}
            sx={{ padding: 0 }}
          >
            <EllipsisVertical size={14} />
          </IconButton>
        ),
      });
    }

    return columns;
  };

  // filter rows - keep deleted rows visible for restore
  const displayRows = combinedData.filter((item) => {
    // Ensure item exists and has an id
    if (!item || !item.id) return false;
    return true;
  });

  // Validate and ensure all rows have valid IDs for DataGrid
  const safeDisplayRows = displayRows.map((row, index) => {
    if (!row.id) {
      console.warn(`Row at index ${index} missing ID, generating UUID:`, row);
      return { ...row, id: uuidv4() };
    }
    return row;
  });

  // Safe getRowId function with fallback
  const safeGetRowId = (row) => {
    if (!row) {
      console.error("Null row passed to getRowId");
      return uuidv4();
    }
    return row.id || uuidv4();
  };

  const handleCellEdit = async (params) => {
    if (!isEditable) return; // Prevent editing if not editable

    const { id, field, value } = params;
    const rowIndex = combinedData.findIndex((row) => row.id === id);

    if (rowIndex === -1) return;

    const updatedData = [...combinedData];
    const itemToUpdate = { ...updatedData[rowIndex] };

    itemToUpdate[field] = value;
    itemToUpdate.humanReview = true;
    itemToUpdate.isHistory = false;
    delete itemToUpdate.isNew;

    if (!itemToUpdate.id) itemToUpdate.id = uuidv4();

    updatedData[rowIndex] = itemToUpdate;
    setCombinedData(updatedData);

    await saveTableData(updatedData, section, patientId, docId);
  };

  // Get status class for visual indicators
  const getStatusClass = () => {
    switch (sectionStatus) {
      case "completed":
        return "section-completed";
      case "processing":
        return "section-processing";
      case "pending":
        return "section-pending";
      default:
        return "";
    }
  };

  const handleCellClick = useCallback(
    (params, event) => {
      const field = section.fields[params.field];

      // Don't handle click for checkbox fields or non-editable fields
      if (!params.isEditable || !field || field.type === "check") {
        return;
      }

      // Ignore portal
      if (event.target?.nodeType === 1 && !event.currentTarget.contains(event.target)) {
        return;
      }

      setCellModesModel((prevModel) => {
        return {
          // Revert the mode of the other cells from other rows
          ...Object.keys(prevModel).reduce(
            (acc, id) => ({
              ...acc,
              [id]: Object.keys(prevModel[id]).reduce(
                (acc2, field) => ({
                  ...acc2,
                  [field]: { mode: GridCellModes.View },
                }),
                {}
              ),
            }),
            {}
          ),
          [params.id]: {
            // Revert the mode of other cells in the same row
            ...Object.keys(prevModel[params.id] || {}).reduce((acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }), {}),
            [params.field]: { mode: GridCellModes.Edit },
          },
        };
      });
    },
    [section.fields]
  );

  const handleCellModesModelChange = useCallback((newModel) => {
    setCellModesModel(newModel);
  }, []);

  const handleCellKeyDown = useCallback(
    (params, event) => {
      // Handle tab navigation
      if (handleTabNavigation(params, event, section, displayRows, setCellModesModel)) {
        return;
      }
    },
    [section, displayRows]
  );

  // Error boundary for DataGrid rendering
  const renderDataGrid = () => {
    try {
      if (!isInitialLoadComplete) {
        return <div className="table-loading-indicator">Loading...</div>;
      }

      if (safeDisplayRows.length === 0) {
        return <div className="table-empty-indicator">No data available</div>;
      }

      return (
        <DataGrid
          key={`datagrid-${sectionKey}`}
          apiRef={apiRef}
          rows={safeDisplayRows}
          columns={createColumns()}
          disableRowSelectionOnClick
          hideFooter={true}
          density="compact"
          getRowId={safeGetRowId}
          disableColumnMenu
          getRowHeight={() => "auto"}
          getRowClassName={(params) => params.row.isDeleted ? "deleted-row" : ""}
          autoHeight
          cellModesModel={cellModesModel}
          onCellModesModelChange={handleCellModesModelChange}
          onCellClick={handleCellClick}
          onCellKeyDown={handleCellKeyDown}
          isCellEditable={(params) => {
            if (!isEditable) return false; // Disable all editing if not editable

            // Allow editing for non-checkbox fields, but not primary field in data style
            const field = section.fields[params.field];
            const isPrimaryField = params.field === section.primaryField;
            const isDataStyle = section.editorStyle === "data";

            return field && field.type !== "check" && !(isPrimaryField && isDataStyle);
          }}
          processRowUpdate={(newRow, oldRow) => {
            if (!isEditable) return oldRow; // Prevent updates if not editable

            // Handle the row update
            const updatedFields = Object.keys(newRow).filter((key) => newRow[key] !== oldRow[key]);

            if (updatedFields.length > 0) {
              updatedFields.forEach((field) => {
                handleCellEdit({ id: newRow.id, field, value: newRow[field] });
              });
            }

            return newRow;
          }}
          onProcessRowUpdateError={(error) => {
            console.error("Row update error:", error);
          }}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              fontSize: "var(--datagrid-header-font-size, var(--text-2xs))",
              fontWeight: 400,
              minHeight: "var(--space-6) !important",
              maxHeight: "var(--space-6) !important",
              marginBottom: "var(--space-1)",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "var(--datagrid-header-font-size, var(--text-2xs))",
            },
            "& .MuiDataGrid-columnHeader": {
              minHeight: "var(--space-6) !important",
              maxHeight: "var(--space-6) !important",
              backgroundColor: "var(--color-table-header) !important",
              paddingLeft: "3px",
              paddingRight: "3px",
              color: "var(--color-text-secondary)",
            },
            "& .MuiDataGrid-columnHeaderRow": {
              minHeight: "var(--space-6) !important",
              maxHeight: "var(--space-6) !important",
            },
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordWrap: "break-word",
              display: "flex",
              alignItems: "flex-start",
              padding: "var(--datagrid-cell-padding, var(--space-1) var(--space-2))",
              border: "none",
              cursor: "pointer",
              fontSize: "var(--datagrid-cell-font-size, var(--text-xs))",
            },
            "& .MuiDataGrid-cell--editing": {
              outlineOffset: "0px !important",
              boxShadow: "none !important",
            },
            "& .MuiDataGrid-row": {
              maxHeight: "none !important",
              borderBottom: "none",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "transparent",
            },
            "& .MuiDataGrid-row--editing": {
              border: "none !important",
              boxShadow: "none !important",
            },
            "& .MuiDataGrid-cell[data-field='actions']": {
              padding: 0,
              justifyContent: "center",
            },
          }}
        />
      );
    } catch (error) {
      console.error("DataGrid render error:", error);
      return <div className="table-error-indicator">Error loading table. Please refresh the page.</div>;
    }
  };

  return (
    <div className={`standard-container ${getStatusClass()}`} key={sectionKey}>
      <div className="edit-header-container">
        <div className="edit-header-left" onClick={toggleAccordion}>
          <h3 className="edit-header">
            <Tooltip title={isOpen ? "Hide section" : "Show section"} arrow>
              <span className={`accordion-icon${isOpen ? " accordion-open" : ""}`}><ChevronDown size={16} /></span>
            </Tooltip>
            {section.title}
          </h3>
        </div>
        <div className="edit-header-right">
          <SectionCommentButton
            sectionKey={section.dataField}
            sectionTitle={section.title}
            onAddSectionComment={isEditable ? onAddSectionComment : undefined}
          />
          {isEditable && isOpen && section.defaultFillButton && section.editorStyle === "data" ? (
            <Tooltip title="Default Fill" arrow>
              <span onClick={defaultFill} className={`copy-icon${defaultFillIcon ? " copied" : ""}`} role="button" tabIndex={0} aria-label="Default fill" onKeyDown={(e) => e.key === 'Enter' && defaultFill()}>
                <FilePlus2 size={14} />
              </span>
            </Tooltip>
          ) : (
            ""
          )}
          {isOpen && !section.excludeCopyIcon ? (
            <Tooltip title="Copy to Clipboard" arrow>
              <span
                onClick={() =>
                  handleCopyToClipboard({
                    section,
                    combinedData,
                    setCopiedIcon,
                    generateSectionExport,
                  })
                }
                className={`copy-icon${copiedIcon ? " copied" : ""}`}
                role="button"
                tabIndex={0}
                aria-label="Copy section to clipboard"
                onKeyDown={(e) => e.key === 'Enter' && handleCopyToClipboard({ section, combinedData, setCopiedIcon, generateSectionExport })}>
                <ClipboardCopy size={14} />
              </span>
            </Tooltip>
          ) : (
            ""
          )}
          {isEditable && isOpen && section.editorStyle === "table" ? (
            <Tooltip title="New Row">
              <CirclePlus className="copy-icon" onClick={handleAddRowLocal} size={14} role="button" tabIndex={0} aria-label="Add new row" onKeyDown={(e) => e.key === 'Enter' && handleAddRowLocal()} />
            </Tooltip>
          ) : (
            ""
          )}
          {/* Show reprocess icon for table sections with AI steps that are empty or have errors */}
          {isEditable && isOpen && onReprocessSection && section.hasAiStep && (() => {
            // Get section data to check for error state
            const sectionData = data?.[section.dataField];
            const hasError = Boolean(sectionData?.error);
            
            // Check if table section is empty (no data or all rows have empty primary fields and summaries)
            const primaryField = section.primaryField || "name";
            const hasContent = (value) => {
              if (!value) return false;
              if (typeof value === "string") return value.trim() !== "";
              return true;
            };
            const hasAnyContent = combinedData?.some(row => 
              hasContent(row?.[primaryField]) || hasContent(row?.summary)
            );
            
            // Show reprocess if section has error OR is empty
            if (!hasError && hasAnyContent) return null;
            
            return (
              <Tooltip title={hasError ? "Reprocess Section (failed)" : "Reprocess Section"} arrow>
                <span
                  onClick={() => {
                    if (sectionStatus !== "processing") {
                      onReprocessSection(section.dataField || section.name || section.id, section.title);
                    }
                  }}
                  className={`reprocess-icon${sectionStatus === "processing" ? " disabled" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-label="Reprocess section"
                  aria-disabled={sectionStatus === "processing"}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && sectionStatus !== "processing") {
                      onReprocessSection(section.dataField || section.name || section.id, section.title);
                    }
                  }}
                  style={{ 
                    marginLeft: "var(--space-2)", 
                    display: "inline-flex", 
                    alignItems: "center", 
                    cursor: sectionStatus === "processing" ? "not-allowed" : "pointer",
                    opacity: sectionStatus === "processing" ? 0.5 : 1
                  }}
                >
                  <RefreshCw size={14} />
                </span>
              </Tooltip>
            );
          })()}
        </div>
      </div>
      {isOpen && (
        <>
          <div className={`table-container${section.editorStyle === "data" ? " table-style-data" : ""}${!isEditable ? " table-readonly" : ""}`}>
            {renderDataGrid()}

            {!isEditable && (
              <div className="table-readonly-notice">
                {!isProcessing && "View only"}
                {isProcessing && sectionStatus === "pending" && "Section will be editable when processing completes"}
                {isProcessing && sectionStatus === "processing" && "Currently being processed..."}
              </div>
            )}
          </div>
          {section.editorStyle === "table" && (
            <Menu
              open={Boolean(menuPosition)}
              onClose={handleMenuClose}
              anchorReference="anchorPosition"
              anchorPosition={menuPosition || undefined}
            >
              {menuRowId && combinedData.find(r => r.id === menuRowId)?.isDeleted ? (
                <MenuItem onClick={() => { handleRestoreRow(menuRowId); handleMenuClose(); }}>
                  <Undo2 size={14} style={{ marginRight: 8 }} /> Restore Row
                </MenuItem>
              ) : pendingDeleteId === menuRowId ? (
                <MenuItem onClick={() => { handleDeleteRow(menuRowId); handleMenuClose(); }}>
                  <CircleCheck size={14} style={{ marginRight: 8, color: 'var(--color-danger)' }} /> Confirm Delete
                </MenuItem>
              ) : (
                <MenuItem onClick={() => { handleDeleteRow(menuRowId); }}>
                  <CircleX size={14} style={{ marginRight: 8 }} /> Delete Row
                </MenuItem>
              )}
              <MenuItem onClick={() => { handleCopyRow(menuRowId); handleMenuClose(); }}>
                <Copy size={14} style={{ marginRight: 8 }} /> Duplicate Row
              </MenuItem>
              <MenuItem onClick={() => { handleAddRowLocal(); handleMenuClose(); }}>
                <CirclePlus size={14} style={{ marginRight: 8 }} /> Add Row
              </MenuItem>
              {onAddRowComment && menuRowId && (
                <MenuItem onClick={() => {
                  const row = combinedData.find(r => r.id === menuRowId);
                  const rowLabel = row?.[section.primaryField] || row?.name || "Row";
                  onAddRowComment(section.dataField, section.title, {
                    type: "row",
                    rowId: menuRowId,
                    rowLabel,
                  });
                  handleMenuClose();
                }}>
                  <MessageSquarePlus size={14} style={{ marginRight: 8 }} /> Add Comment
                </MenuItem>
              )}
            </Menu>
          )}
          {/* History Popup Popover */}
          <Popover
            open={Boolean(historyAnchorEl)}
            anchorEl={historyAnchorEl}
            onClose={() => { setHistoryAnchorEl(null); setHistoryPopoverKey(null); }}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{ paper: { className: "history-popover-paper" } }}
          >
            {historyPopoverKey && (() => {
              const currentRow = combinedData.find(r => r[section.primaryField] === historyPopoverKey);
              const currentSummary = currentRow?.isHistory ? "" : currentRow?.summary || "";
              const entries = rowHistoryMap.get(historyPopoverKey) || [];

              return (
                <div className="history-popover-content">
                  <div className="history-popover-header">
                    <div className="history-popover-title">{historyPopoverKey}</div>
                    <button
                      className="history-popover-close"
                      onClick={() => { setHistoryAnchorEl(null); setHistoryPopoverKey(null); }}
                      aria-label="Close history"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {currentSummary && (
                    <div className="history-popover-entry history-popover-current">
                      <div className="history-popover-label">This note</div>
                      <div className="history-popover-summary">{currentSummary}</div>
                    </div>
                  )}
                  {entries.map((entry, idx) => (
                    <div key={idx} className="history-popover-entry">
                      <div className="history-popover-label">
                        {formatEncounterDate(entry.date)}
                        {entry.reviewState === 'unreviewed' && ' · Unreviewed note'}
                        {entry.sourceDocumentId && <button type="button" className="history-source-link" onClick={() => {
                          setHistoryAnchorEl(null); setHistoryPopoverKey(null);
                          navigate('/document', { state: { patientId, docId: entry.sourceDocumentId } });
                        }}>Open note</button>}
                      </div>
                      <div className="history-popover-summary">{entry.summary}</div>
                    </div>
                  ))}
                  {entries.length === 0 && !currentSummary && (
                    <div className="history-popover-empty">No historical data</div>
                  )}
                </div>
              );
            })()}
          </Popover>
        </>
      )}
    </div>
  );
};

export default React.memo(EditableTableSection);
